import { SxProps, Theme } from '@mui/system';
import { brandAlpha } from "../../../theme/mixins";

export const pageHeaderContainerStyles: SxProps<Theme> = {
    mb: 4,
};

export const pageHeaderTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.015em',
};

export const pageHeaderSubtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

/**
 * One card, not two.
 *
 * There were two, side by side, split by whether their contents could be edited. The split
 * cost more than it bought: it forced the account facts into a narrow sidebar and their
 * labels into a stack, and it put the picture and the name that belongs under it in
 * different boxes. Everything the page has to say about one account now sits in one card.
 */
export const pageLayoutContainerStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
};

export const leftCardContentStyles: SxProps<Theme> = {
    p: 4,
};

export const sectionHeaderContainerStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mb: 3,
};

export const sectionHeaderIconStyles = (theme: Theme): SxProps<Theme> => ({
    color: theme.palette.primary.main,
});

export const sectionHeaderTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.01em',
};

/**
 * One grid for the whole form, so the space between the rows matches the space
 * between the columns. The phone keeps a single column rather than spanning:
 * it is as short as a first name, and a full-width box for ten digits reads as
 * a different kind of field to the two above it.
 */
export const formGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 3,
};

export const submitButtonContainerStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 3,
};

/** Who the page is about, centred over everything it then says about them. */
export const accountSummaryStyles: SxProps<Theme> = {
    textAlign: 'center',
    mb: 4,
};

export const accountHeadingStyles: SxProps<Theme> = {
    fontWeight: 600,
};

/**
 * The two read-only facts, at the far end of the picture's own row.
 *
 * Label and value on one line each, and the pairs are *direct children* of one grid rather
 * than each being boxed together -- the same construction `RecordCard` uses, and for the
 * reason its comment gives: that is what makes the values line up however long the labels
 * are. Boxing a pair would make it a single cell and take the alignment away.
 *
 * `auto auto` rather than `auto 1fr` because this block is sized by its contents and pushed
 * to the right by `ml: auto`, so a greedy second column would stretch it back across the
 * card. Below `sm` the row becomes a column and this sits under the buttons, centred with
 * everything else.
 */
export const accountFieldsStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'auto auto' },
    columnGap: 3,
    rowGap: 1,
    alignItems: 'center',
    ml: { sm: 'auto' },
};

export const accountLabelStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
};

/**
 * The one editable avatar in the app, at the size the profile page draws it.
 *
 * Deliberately not `UserAvatar`: this one is 88px, carries an upload control beside it and a
 * spinner over it, and is `primary` rather than the `secondary` every drawn-only avatar uses
 * -- the difference is the point, since this is a control and those are labels.
 *
 * No `mx: 'auto'` and no `mb`, which it had while it sat centred in the account card. It now
 * leads a row in the editable card, so the row owns the spacing.
 */
export const pictureAvatarStyles = (theme: Theme): SxProps<Theme> => ({
    width: 88,
    height: 88,
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: '2rem',
    fontWeight: 700,
    boxShadow: `0 8px 20px ${brandAlpha('primary', 0.28)}`,
});

/**
 * The picture beside what you can do to it.
 *
 * A row rather than the centred stack it used to be, because it has moved into the wide card:
 * the two cards divide by what they are for -- this one is everything you can change, the
 * other is everything the account merely says -- so the picture sits with the name fields
 * rather than above a list of read-only labels. Stacks below `sm`, where a row would leave
 * the buttons about eighty pixels wide.
 */
export const pictureSectionStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: 'center',
    textAlign: { xs: 'center', sm: 'left' },
    gap: 3,
    mb: 3,
};

/** Holds the buttons beside the picture, and lets them shrink rather than widen the row. */
export const pictureDetailStyles: SxProps<Theme> = {
    minWidth: 0,
};

export const pictureSectionDividerStyles: SxProps<Theme> = {
    mb: 3,
};

/** Positions the spinner over the avatar while an upload or a removal is in flight. */
export const pictureFrameStyles: SxProps<Theme> = {
    position: 'relative',
    display: 'inline-flex',
    flexShrink: 0,
};

export const pictureBusyStyles: SxProps<Theme> = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.72)',
};

/**
 * The two buttons stacked beside the picture rather than strung out along it.
 *
 * A column because they are one control group about one thing: side by side they read as two
 * unrelated actions spread across the card, and "Remove picture" ends up sitting under the
 * Last Name field it has nothing to do with. `flex-start` rather than `stretch` so each
 * button is only as wide as its own label.
 */
export const pictureActionsStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'center', sm: 'flex-start' },
    gap: 0.5,
};

export const usernameLabelIconStyles = (theme: Theme): SxProps<Theme> => ({
    fontSize: 16,
    mr: 1,
    color: theme.palette.grey[600],
});

export const memberSinceIconStyles = usernameLabelIconStyles;

export const strongBodyTextStyles: SxProps<Theme> = {
    fontWeight: 500,
};
